/**
 * doctors-modal-form.js — doctors 新增醫師 Modal 的文字/選單/textarea 欄位（formField 執行期產出）
 *
 * Pilot ①（表單元件化第一個遷移）：證明 field-factory 的 formField() 在 modal 動態場景實戰可用。
 * 把原本手寫於 doctors.html 的 5 個 .form-field（姓名 / 科別 / 字號 / 學經歷 / 專業領域介紹）
 * 改以 formField(spec) 於執行期產出並掛載；upload 欄位（頭像 / 執照照片 / 學歷證書）不經 factory
 * （factory 不產 .upload-field 殼，upload UX 由 admin-form-edit.js 的 initUploads 接管），故仍為
 * doctors.html 靜態 markup，只在 HTML 端對齊 id 到 UPLOAD_RULES key。
 *
 * 規則宣告（data-rules，由 field-rules.js 規則層在 blur/submit 解；首次活化規則插槽）：
 *   - 醫師姓名：required + no-blank
 *   - 執業科別：required（HTML5 valueMissing；placeholder option disabled）
 *   - 醫師執照字號：required + no-blank + license-no（衛福部比對關鍵法遵欄）
 *
 * ── 載入時序（關鍵）──
 *   本模組於 admin.js 中 **admin-form-edit.js 之前** import。module script 為 defer：執行時 DOM
 *   已解析（[data-doctor-modal-fields] 必存在），且 admin-form-edit.js init() 於其 module-eval 立即
 *   執行（readyState 已 'interactive'）。故「先產欄位、後 init 掃 data-rules / 綁 blur」順序由 import
 *   次序保證 → 引擎掃到的是已含 data-rules、已串好 error 節點的完整欄位。
 *   無 [data-doctor-modal-fields]（其他頁）即早退 no-op。
 */

import { formField } from '../components/field-factory.js';

/* 5 個欄位 spec（順序＝原 markup：姓名 / 科別 / 字號 / 學經歷 / 專業領域介紹）。
 * id / name / placeholder / maxlength 與原 doctors.html 手寫值逐一對齊，僅補 data-rules。 */
const FIELD_SPECS = [
  {
    // 1 醫師姓名（必填，左半欄）
    type: 'text',
    id: 'doctor-name',
    name: 'name',
    label: '醫師姓名',
    placeholder: '例：陳信宏',
    required: true,
    rules: 'no-blank',
  },
  {
    // 2 執業科別（必填，右半欄）；placeholder option disabled，required 走 HTML5 valueMissing
    type: 'select',
    id: 'doctor-specialty',
    name: 'specialty',
    label: '執業科別',
    required: true,
    optionsPlaceholder: '請選擇',
    options: [
      ['dermatology', '皮膚科'],
      ['plastic-surgery', '整形外科'],
      ['aesthetic', '醫學美容'],
      ['other', '其他'],
    ],
  },
  {
    // 3 醫師執照字號（必填，mono，左半欄，+hint）；衛福部比對關鍵欄
    type: 'text',
    id: 'doctor-license-no',
    name: 'licenseNo',
    label: '醫師執照字號',
    placeholder: '例：醫字第 123456 號',
    required: true,
    mono: true,
    rules: 'no-blank license-no',
    hint: '平台將依此字號比對衛福部資料進行驗證',
  },
  {
    // 4 學經歷（選填，跨兩欄）
    type: 'text',
    id: 'doctor-bio',
    name: 'bio',
    label: '學經歷',
    placeholder: '例：台灣大學醫學院醫學系、台大醫院皮膚科住院醫師',
    maxlength: 100,
    className: 'cr-form-card__grid-full',
  },
  {
    // 5 專業領域介紹（選填，textarea，跨兩欄）
    type: 'textarea',
    id: 'doctor-field',
    name: 'field',
    label: '專業領域介紹',
    placeholder: '請說明醫師的專業領域與擅長項目…',
    rows: 3,
    maxlength: 300,
    className: 'cr-form-card__grid-full',
  },
];

function buildModalFields(root = document) {
  const mount = root.querySelector('[data-doctor-modal-fields]');
  if (!mount) return; // 非 doctors 頁 → no-op

  // 冪等：已產過則不重產（避免 HMR / 重入時欄位翻倍）。
  if (mount.dataset.fieldsBuilt === 'true') return;

  // 批次掛載（DocumentFragment 一次 append，不在迴圈內逐次 reflow）。
  const frag = document.createDocumentFragment();
  FIELD_SPECS.forEach((spec) => frag.appendChild(formField(spec)));
  mount.appendChild(frag);
  mount.dataset.fieldsBuilt = 'true';
}

// module script 為 defer：DOM 已解析。仍守 readyState 以相容非 module 載入情境。
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => buildModalFields(), { once: true });
} else {
  buildModalFields();
}

export { buildModalFields };
